import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../../../api/cabinet";
import CabIcon from "../CabIcon";
import UserAvatar from "../UserAvatar";
import { Button, Input, Pill, Tabs, EmptyState, Skeleton, Spinner } from "../../ui";
import "../../../styles/cabinet-friends.css";

/* вкладки. «Чаты» убраны 2026-07-09: переписка переехала в Маську —
 * мессенджер экосистемы (аккаунт единый, user_id общий). Тут остались
 * друзья/запросы/поиск, у друга кнопка «Написать в Маське» (?dm=<id>). */
const TABS = [
  { id: "friends", title: "Друзья", icon: "users" },
  { id: "requests", title: "Запросы", icon: "inbox" },
  { id: "search", title: "Поиск", icon: "search" },
];

/** Ссылка «написать юзеру в Маське»: диплинк ?dm=<id> открывает/создаёт
 * личный чат (processDeepLink в App.jsx Маськи). */
const maskaDmUrl = (userId) => `https://маська.рф/?dm=${userId}`;

/**
 * FriendsScreen — экран «Друзья». Порт FriendsScreen.kt.
 * hero → табы (Друзья/Запросы/Поиск) → контент.
 */
export default function FriendsScreen() {
  const [activeTab, setActiveTab] = useState("friends");

  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* загрузка вкладки */
  useEffect(() => {
    if (activeTab === "search") return;
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        if (activeTab === "friends") {
          setFriends(await api.getFriends(ctrl.signal));
        } else if (activeTab === "requests") {
          const r = await api.getFriendRequests(ctrl.signal);
          setIncoming(r.incoming);
          setOutgoing(r.outgoing);
        }
        setLoading(false);
      } catch (e) {
        if (e.name === "AbortError") return;
        setError(e.message || "Ошибка загрузки");
        setLoading(false);
      }
    };
    load();
    return () => ctrl.abort();
  }, [activeTab]);

  /* Действия с запросами.
     Раньше оба обработчика имели пустой `catch {}`: при сбое сети строка
     не исчезала, ошибки не было — человек жал «Принять» снова и снова,
     и каждый раз выглядело так, будто кнопка сломана. Теперь у операции
     есть состояние (какая строка занята) и видимая ошибка. */
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState("");

  const handleAccept = async (req) => {
    if (busyId) return;
    setBusyId(req.request_id);
    setActionError("");
    try {
      await api.acceptFriendRequest(req.request_id);
      setIncoming((p) => p.filter((r) => r.request_id !== req.request_id));
    } catch {
      setActionError("Не получилось принять запрос. Попробуй ещё раз.");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (req, isOutgoing) => {
    if (busyId) return;
    setBusyId(req.request_id);
    setActionError("");
    try {
      await api.rejectFriendRequest(req.request_id);
      if (isOutgoing)
        setOutgoing((p) => p.filter((r) => r.request_id !== req.request_id));
      else setIncoming((p) => p.filter((r) => r.request_id !== req.request_id));
    } catch {
      setActionError(
        isOutgoing
          ? "Не получилось отозвать запрос. Попробуй ещё раз."
          : "Не получилось отклонить запрос. Попробуй ещё раз."
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rb-cab-screen rb-fr">
      {/* ── hero ───────────────────────────────────────────────── */}
      <div className="rb-fr__hero">
        <h1 className="rb-fr__hero-title">
          <CabIcon name="users" size={24} /> Друзья
        </h1>
        <p className="rb-fr__hero-sub">Друзья, запросы и поиск игроков</p>
      </div>

      {/* Вкладки — разделы с разным содержимым, значит Tabs, а не чипы.
          Счётчик входящих прямо в подписи: иначе о запросе узнаёшь,
          только заглянув во вкладку. */}
      <Tabs
        items={TABS.map((t) => ({
          id: t.id,
          label:
            t.id === "requests" && incoming.length > 0
              ? `${t.title} · ${incoming.length}`
              : t.title,
          icon: <CabIcon name={t.icon} size={16} />,
        }))}
        value={activeTab}
        onChange={setActiveTab}
        className="rb-fr__tabs"
      />

      {actionError && <div className="rb-fr__action-error">{actionError}</div>}

      {/* ── контент ────────────────────────────────────────────── */}
      <div className="rb-fr__content">
        {activeTab === "friends" && (
          <FriendsTab
            loading={loading}
            error={error}
            friends={friends}
            onFind={() => setActiveTab("search")}
          />
        )}
        {activeTab === "requests" && (
          <RequestsTab
            loading={loading}
            error={error}
            incoming={incoming}
            outgoing={outgoing}
            onAccept={handleAccept}
            onReject={handleReject}
            busyId={busyId}
            onFind={() => setActiveTab("search")}
          />
        )}
        {activeTab === "search" && <SearchTab />}
      </div>
    </div>
  );
}

/* ── общие ─────────────────────────────────────────────────────────
   Здесь жили три локальные копии того, что есть в UI-слое: свой Avatar,
   свой Loader на .rb-cab-spinner и свой EmptyState — причём последний
   назывался ТАК ЖЕ, как общий, но принимал `subtitle` вместо `desc`
   и не имел слота под кнопку. Из-за этого все пустые состояния экрана
   оказывались тупиками: «Пока нет друзей» без единого способа их найти.
   Теперь используются Avatar → UserAvatar, Loader → Skeleton,
   EmptyState → общий из ../../ui. */

/** Скелетон списка: строки той же формы, что настоящие. */
function RowsSkeleton({ rows = 5 }) {
  return (
    <div className="rb-fr__list" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="rb-fr__row rb-fr__row--skeleton">
          <Skeleton w={40} h={40} r="50%" />
          <div className="rb-fr__row-sk">
            <Skeleton w="44%" h={14} />
            <Skeleton w="26%" h={11} />
          </div>
          <Skeleton w={92} h={30} r="var(--r-pill)" />
        </div>
      ))}
    </div>
  );
}

/* ── промо «переписка в Маське» ────────────────────────────────────
 * Вместо встроенных чатов (убраны 2026-07-09): общение экосистемы живёт
 * в Маське, аккаунт единый — жми «Написать» у друга или открой Маську. */
function MaskaPromo() {
  return (
    <a
      className="rb-fr__maska"
      href="https://маська.рф"
      target="_blank"
      rel="noopener noreferrer"
      title="Открыть мессенджер Маська"
    >
      <span className="rb-fr__maska-ico">
        <CabIcon name="chat" size={22} />
      </span>
      <span className="rb-fr__maska-body">
        <span className="rb-fr__maska-title">Переписка — в Маське</span>
        <span className="rb-fr__maska-sub">
          Чаты Рублокса переехали в Маську — мессенджер нашей экосистемы.
          Аккаунт тот же: жми «Написать» у друга, и откроется ваш чат.
        </span>
      </span>
      <CabIcon name="arrowRight" size={16} />
    </a>
  );
}

/* ── вкладка «Друзья» ──────────────────────────────────────────── */
function FriendsTab({ loading, error, friends, onFind }) {
  const navigate = useNavigate();
  if (loading) return <RowsSkeleton />;
  if (error)
    return (
      <EmptyState
        icon={<CabIcon name="warning" size={40} />}
        title="Не получилось загрузить"
        desc="Проверь соединение и обнови страницу."
      />
    );
  return (
    <>
      <MaskaPromo />
      {friends.length === 0 ? (
        /* Пустое состояние с выходом: раньше оно лишь советовало
           «найди на вкладке Поиск», но перейти туда было нечем. */
        <EmptyState
          icon={<CabIcon name="users" size={40} />}
          title="Пока никого нет"
          desc="Найди игроков и отправь им запрос — вместе играть интереснее."
          action={
            <Button variant="primary" onClick={onFind}>
              Найти игроков
            </Button>
          }
        />
      ) : (
        <div className="rb-fr__list">
          {friends.map((f) => (
            <div
              key={f.user_id}
              className="rb-fr__row"
              onClick={() => navigate(`/app/profile/${f.user_id}`)}
              title="Открыть профиль"
            >
              <UserAvatar name={f.name} photo={api.avatarSrc(f.photo_thumb_b64)} size={40} />
              <div className="rb-fr__row-info">
                <span className="rb-fr__row-name">
                  {f.name || `Игрок #${f.user_id}`}
                </span>
                {f.level > 0 && (
                  <span className="rb-fr__row-sub">Уровень {f.level}</span>
                )}
              </div>
              <a
                className="rb-fr__chip rb-fr__chip--maska"
                href={maskaDmUrl(f.user_id)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title="Открыть чат с этим игроком в Маське"
              >
                <CabIcon name="chat" size={13} /> Написать
              </a>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ── вкладка «Запросы» ─────────────────────────────────────────── */
function RequestsTab({
  loading, error, incoming, outgoing, onAccept, onReject, busyId, onFind,
}) {
  if (loading) return <RowsSkeleton rows={3} />;
  if (error)
    return (
      <EmptyState
        icon={<CabIcon name="warning" size={40} />}
        title="Не получилось загрузить"
        desc="Проверь соединение и обнови страницу."
      />
    );
  if (incoming.length === 0 && outgoing.length === 0)
    return (
      <EmptyState
        icon={<CabIcon name="inbox" size={40} />}
        title="Запросов пока нет"
        desc="Когда кто-то захочет добавиться — увидишь здесь."
        action={
          <Button variant="ghost" onClick={onFind}>
            Найти игроков
          </Button>
        }
      />
    );
  return (
    <div className="rb-fr__list">
      {incoming.length > 0 && (
        <>
          <div className="rb-fr__sec">
            ТЕБЕ ХОТЯТ ДОБАВИТЬСЯ ({incoming.length})
          </div>
          {incoming.map((req) => (
            <RequestRow
              key={req.request_id}
              req={req}
              primary="Принять"
              secondary="Отклонить"
              onPrimary={() => onAccept(req)}
              onSecondary={() => onReject(req, false)}
              busy={busyId === req.request_id}
            />
          ))}
        </>
      )}
      {outgoing.length > 0 && (
        <>
          <div className="rb-fr__sec">ТЫ ОТПРАВИЛ ({outgoing.length})</div>
          {outgoing.map((req) => (
            <RequestRow
              key={req.request_id}
              req={req}
              secondary="Отозвать"
              onSecondary={() => onReject(req, true)}
              busy={busyId === req.request_id}
            />
          ))}
        </>
      )}
    </div>
  );
}

function RequestRow({ req, primary, secondary, onPrimary, onSecondary, busy }) {
  return (
    <div className="rb-fr__row">
      <UserAvatar name={req.name} photo={api.avatarSrc(req.photo_thumb_b64)} size={40} />
      <div className="rb-fr__row-info">
        <span className="rb-fr__row-name">
          {req.name || `Игрок #${req.user_id}`}
        </span>
        {req.level > 0 && (
          <span className="rb-fr__row-sub">Уровень {req.level}</span>
        )}
      </div>
      {/* Кнопки со своим loading: на медленной сети действие занимало
          секунды без единого признака, и человек жал повторно. */}
      {primary && (
        <Button variant="primary" size="sm" onClick={onPrimary} loading={busy}>
          {primary}
        </Button>
      )}
      <Button variant="ghost" size="sm" onClick={onSecondary} disabled={busy}>
        {secondary}
      </Button>
    </div>
  );
}

/* ── вкладка «Поиск» ───────────────────────────────────────────── */
function SearchTab() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sent, setSent] = useState(new Set());

  const [searchError, setSearchError] = useState("");
  const [adding, setAdding] = useState(null);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      /* Сброс searching обязателен: если стереть текст во время запроса,
         спиннер оставался крутиться навсегда. */
      setSearching(false);
      setSearchError("");
      return undefined;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setSearching(true);
      setSearchError("");
      try {
        const r = await api.searchUsers(query.trim(), ctrl.signal);
        /* Проверка signal перед записью: ответ отменённого запроса мог
           прийти уже после старта нового и перезаписать свежие результаты
           устаревшими. */
        if (!ctrl.signal.aborted) setResults(r);
      } catch (e) {
        // AbortError — это не сбой, а наша же отмена: молчим.
        if (e?.name !== "AbortError") {
          setSearchError("Поиск не сработал. Попробуй ещё раз.");
        }
      } finally {
        if (!ctrl.signal.aborted) setSearching(false);
      }
    }, 300);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [query]);

  /* Отправка запроса в друзья.
     Раньше `setSent` вызывался ДО запроса и никогда не откатывался:
     при ошибке навсегда оставалось «Запрос отправлен», хотя запрос
     не ушёл — обновив вкладку, человек обнаруживал обратное. */
  const handleAdd = async (user) => {
    if (adding) return;
    setAdding(user.user_id);
    setSearchError("");
    try {
      await api.sendFriendRequest(user.user_id);
      setSent((p) => new Set([...p, user.user_id]));
    } catch {
      setSearchError("Не получилось отправить запрос. Попробуй ещё раз.");
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className="rb-fr__search">
      <Input
        className="rb-fr__search-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="имя или почта…"
        leadIcon={<CabIcon name="search" size={17} />}
        trailing={searching ? <Spinner size="sm" /> : undefined}
        aria-label="Поиск игроков"
      />

      {searchError && <div className="rb-fr__action-error">{searchError}</div>}

      {query.trim().length < 2 ? (
        <EmptyState
          icon={<CabIcon name="search" size={40} />}
          title="Найди друзей"
          desc="Введи минимум две буквы имени или почту."
        />
      ) : searching && results.length === 0 ? (
        <RowsSkeleton rows={3} />
      ) : results.length === 0 ? (
        <EmptyState
          icon={<CabIcon name="shrug" size={40} />}
          title="Никого не нашли"
          desc="Проверь написание — или попроси у друга почту, по ней найдётся точно."
        />
      ) : (
        <div className="rb-fr__list">
          {results.map((u) => {
            const isSent =
              sent.has(u.user_id) || u.friendship_status === "request_sent";
            const isFriend = u.friendship_status === "friends";
            const received = u.friendship_status === "request_received";
            return (
              <div key={u.user_id} className="rb-fr__row">
                <UserAvatar name={u.name} photo={api.avatarSrc(u.photo_thumb_b64)} size={40} />
                <div className="rb-fr__row-info">
                  <span className="rb-fr__row-name">
                    {u.name || `Игрок #${u.user_id}`}
                  </span>
                  {u.level > 0 && (
                    <span className="rb-fr__row-sub">Уровень {u.level}</span>
                  )}
                </div>
                {isFriend ? (
                  <Pill tone="success" icon={<CabIcon name="check" size={13} />}>
                    В друзьях
                  </Pill>
                ) : received ? (
                  <Pill tone="warning">Ждёт твоего ответа</Pill>
                ) : isSent ? (
                  <Pill tone="soft">Запрос отправлен</Pill>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleAdd(u)}
                    loading={adding === u.user_id}
                    icon={<CabIcon name="plus" size={14} />}
                  >
                    Добавить
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
